/*
 * PEGASI motion system
 * ---------------------------------------------------------------------------
 * A single engine for every Pegasi page. Declarative: markup opts in with
 * data attributes, this file owns all the timing, scheduling and cleanup.
 *
 *   data-pg-m="rise|mask|mask-x|frame"   entrance primitive
 *   data-pg-m-delay="120"                 stagger offset in ms
 *   data-pg-split="lines"                 split into masked lines, then reveal
 *   data-pg-parallax="0.25"               scrubbed depth, transform only
 *   data-pg-magnetic                      cursor attraction
 *   data-pg-inertia                       leans with scroll velocity
 *   data-pg-cursor="Drag"                 contextual cursor label
 *   data-pg-count="1250"                  numeric count-up
 *
 * ---------------------------------------------------------------------------
 * ON RENDER QUALITY
 *
 * Animation degrades type and imagery in two specific ways, and both are
 * avoidable:
 *
 *   1. A composited layer loses subpixel antialiasing. Text inside anything
 *      carrying a transform, an opacity below 1, or will-change renders with
 *      greyscale AA and looks noticeably softer. The fix is not to avoid
 *      animating, it is to DE-PROMOTE the moment the animation ends: strip
 *      will-change and remove the inline transform so the element returns to
 *      normal painting. That is what releaseLayer() does, and every primitive
 *      routes through it.
 *
 *   2. Images rasterise once at their layer size. Scaling UP during an
 *      animation resamples that raster and softens the image permanently for
 *      the duration. So the "frame" primitive only ever scales DOWN (1.12 to
 *      1), which starts oversampled and lands exact.
 *
 * Line splitting also waits on document.fonts.ready. Splitting before the web
 * font loads measures the fallback, so lines break in the wrong places and
 * visibly reflow when the real face arrives.
 * ---------------------------------------------------------------------------
 */
(() => {
  'use strict';

  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  if (reduce.matches) {
    // Still mark ready so the "hidden until JS" guard releases.
    root.classList.add('pg-m-ready');
    return;
  }

  /* =======================================================================
     TIER
     Not every device should get every layer. Rather than guessing from
     screen width, read what the platform actually reports and let the frame
     monitor demote further if the device turns out to be slower than it
     claims.
     ======================================================================= */
  const nav = navigator;
  const saveData = nav.connection && nav.connection.saveData;
  const cores = nav.hardwareConcurrency || 4;
  const memory = nav.deviceMemory || 4;

  let tier = 'high';
  if (saveData || cores <= 2 || memory <= 2) tier = 'low';
  else if (cores <= 4 || memory <= 4 || !finePointer.matches) tier = 'medium';
  root.setAttribute('data-pg-tier', tier);

  /* =======================================================================
     QUALITY GUARD
     ======================================================================= */

  /**
   * Return an element to normal, non-composited painting. Called the instant
   * an entrance finishes. Without this, every revealed heading on the page
   * keeps a compositing layer for the rest of the session and renders with
   * greyscale antialiasing.
   */
  const releaseLayer = (el) => {
    el.style.willChange = '';
    // Only clear transforms we own. Inline transform is set by this engine.
    if (el.style.transform) el.style.transform = '';
  };

  /** Promote just before animating, never speculatively. */
  const promoteLayer = (el, props) => {
    el.style.willChange = props || 'transform, opacity';
  };

  const onceTransitionEnd = (el, done) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      el.removeEventListener('transitionend', handler);
      done();
    };
    const handler = (event) => {
      if (event.target !== el) return;
      finish();
    };
    el.addEventListener('transitionend', handler);
    // transitionend does not fire if the property never changes or the element
    // is offscreen when it lands. Belt and braces so layers are always freed.
    setTimeout(finish, 2600);
  };

  /* =======================================================================
     FRAME LOOP
     One rAF for the entire page. Every consumer registers a callback; nobody
     else is allowed to start a loop. All DOM reads happen in the scroll
     handler, all writes happen in the frame, so we never thrash layout.
     ======================================================================= */
  const frameTasks = new Set();
  let running = false;
  let lastTime = 0;

  const tick = (now) => {
    const delta = Math.min((now - lastTime) / 1000, 0.064) || 0.016;
    lastTime = now;
    frameTasks.forEach((fn) => fn(delta, now));
    if (frameTasks.size) requestAnimationFrame(tick);
    else running = false;
  };

  const addFrameTask = (fn) => {
    frameTasks.add(fn);
    if (!running) {
      running = true;
      lastTime = performance.now();
      requestAnimationFrame(tick);
    }
  };

  /* =======================================================================
     SCROLL SIGNALS
     Publishes velocity and page progress as CSS custom properties so the
     stylesheet can react without any element needing its own listener.
     ======================================================================= */
  const scroll = { y: window.scrollY, last: window.scrollY, v: 0, vSmooth: 0 };

  let lastPublishedV = 0;
  let lastPublishedP = 0;
  let idleFrames = 0;

  /**
   * Scroll-driven work: publishes the signals and drives parallax.
   *
   * Critically this task REMOVES ITSELF once the page settles. The first
   * version left it registered permanently, which kept a requestAnimationFrame
   * loop running at 60fps for the entire session even on a page sitting
   * completely still. It is re-registered on the next scroll event.
   */
  const scrollTask = (delta) => {
    const moved = scroll.y !== scroll.last;
    const raw = (scroll.y - scroll.last) / Math.max(delta, 0.001);
    scroll.last = scroll.y;

    // Normalise to roughly -1..1 for a brisk scroll, then smooth it so the
    // signal has weight instead of snapping.
    const target = Math.max(-1, Math.min(1, raw / 2600));
    scroll.vSmooth += (target - scroll.vSmooth) * (1 - Math.exp(-8 * delta));

    const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    const progress = Math.min(1, scroll.y / max);

    // Quantise hard before writing. A custom property write on :root
    // invalidates style for the whole document, and at 0.001 granularity that
    // fired on nearly every frame of every scroll. These are ambient signals,
    // not animation drivers, so coarse steps are plenty.
    const v = Math.round(scroll.vSmooth * 50) / 50;
    const p = Math.round(progress * 200) / 200;
    if (v !== lastPublishedV) {
      root.style.setProperty('--pg-scroll-v', String(v));
      lastPublishedV = v;
    }
    if (p !== lastPublishedP) {
      root.style.setProperty('--pg-scroll-p', String(p));
      lastPublishedP = p;
    }

    runParallax(delta);

    // Settle detection: nothing moved, velocity has decayed, parallax has
    // arrived. Give it a short grace period, then stop the loop entirely.
    if (!moved && Math.abs(scroll.vSmooth) < 0.002 && parallaxSettled()) idleFrames++;
    else idleFrames = 0;
    if (idleFrames > 20) {
      idleFrames = 0;
      frameTasks.delete(scrollTask);
    }
  };

  const wakeScroll = () => {
    scroll.y = window.scrollY;
    idleFrames = 0;
    if (!frameTasks.has(scrollTask)) addFrameTask(scrollTask);
  };

  window.addEventListener('scroll', wakeScroll, { passive: true });
  window.addEventListener('resize', wakeScroll, { passive: true });

  /* =======================================================================
     SPLIT TYPE
     Wraps visual lines so each can rise from behind its own mask. Uses Range
     rectangles to find real line boxes, which means it respects whatever the
     font and container actually produced rather than guessing at word counts.
     ======================================================================= */
  const splitTargets = [];

  const splitLines = (el) => {
    const original = el.getAttribute('data-pg-split-src') || el.innerHTML;
    el.setAttribute('data-pg-split-src', original);

    // Measure with a throwaway clone of the text nodes.
    const text = el.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return;

    el.innerHTML = '';
    const probe = document.createElement('span');
    text.split(' ').forEach((word, i) => {
      const w = document.createElement('span');
      w.textContent = word;
      w.style.display = 'inline-block';
      probe.appendChild(w);
      if (i !== text.split(' ').length - 1) probe.appendChild(document.createTextNode(' '));
    });
    el.appendChild(probe);

    // Group words by their vertical offset: same top means same line.
    const lines = [];
    let currentTop = null;
    let current = [];
    probe.querySelectorAll('span').forEach((w) => {
      const top = Math.round(w.getBoundingClientRect().top);
      if (currentTop === null || Math.abs(top - currentTop) > 2) {
        if (current.length) lines.push(current);
        current = [w.textContent];
        currentTop = top;
      } else {
        current.push(w.textContent);
      }
    });
    if (current.length) lines.push(current);

    el.innerHTML = '';
    lines.forEach((words) => {
      const outer = document.createElement('span');
      outer.className = 'pg-line';
      const inner = document.createElement('span');
      inner.className = 'pg-line__i';
      inner.textContent = words.join(' ');
      outer.appendChild(inner);
      el.appendChild(outer);
    });
  };

  const applyLineDelays = (el) => {
    const step = parseFloat(el.getAttribute('data-pg-split-stagger') || '70');
    el.querySelectorAll('.pg-line__i').forEach((inner, i) => {
      inner.style.setProperty('--pg-m-delay', i * step + 'ms');
    });
  };

  /* =======================================================================
     REVEAL
     ======================================================================= */
  const revealed = new WeakSet();

  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          io.unobserve(el);
          if (revealed.has(el)) return;
          revealed.add(el);

          promoteLayer(el);
          // Next frame, so the promotion lands before the class flips.
          requestAnimationFrame(() => {
            el.classList.add('is-in');
            const inners = el.querySelectorAll('.pg-line__i');
            const last = inners.length ? inners[inners.length - 1] : el;
            onceTransitionEnd(last, () => {
              releaseLayer(el);
              inners.forEach(releaseLayer);
              // Once the mask has finished there is no reason to keep the
              // clipping container: removing overflow restores normal text
              // rendering and lets descenders paint freely.
              el.querySelectorAll('.pg-line').forEach((line) => {
                line.style.overflow = 'visible';
              });
            });
          });
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 })
    : null;

  /* =======================================================================
     PARALLAX
     ======================================================================= */
  const parallaxItems = [];

  const registerParallax = (el) => {
    const depth = parseFloat(el.getAttribute('data-pg-parallax')) || 0.2;
    parallaxItems.push({ el, depth, current: 0, target: 0, rect: null });
  };

  const measureParallax = () => {
    parallaxItems.forEach((item) => {
      const r = item.el.getBoundingClientRect();
      item.rect = { top: r.top + window.scrollY, height: r.height };
    });
  };

  /* =======================================================================
     MAGNETIC
     ======================================================================= */
  const magnets = [];

  const registerMagnet = (el) => {
    const strength = parseFloat(el.getAttribute('data-pg-magnetic')) || 0.35;
    const m = { el, strength, x: 0, y: 0, tx: 0, ty: 0, active: false };
    magnets.push(m);

    el.addEventListener('pointerenter', () => {
      m.active = true;
      promoteLayer(el, 'transform');
      wakeMagnets();
    });
    el.addEventListener('pointermove', (event) => {
      const b = el.getBoundingClientRect();
      m.tx = (event.clientX - (b.left + b.width / 2)) * m.strength;
      m.ty = (event.clientY - (b.top + b.height / 2)) * m.strength;
    });
    el.addEventListener('pointerleave', () => {
      m.active = false;
      m.tx = 0;
      m.ty = 0;
      wakeMagnets();
    });
  };

  /* =======================================================================
     COUNTER
     ======================================================================= */
  const registerCounter = (el) => {
    const target = parseFloat(el.getAttribute('data-pg-count'));
    if (isNaN(target)) return;
    const pad = el.getAttribute('data-pg-count-pad');
    let started = false;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || started) return;
        started = true;
        obs.disconnect();
        const dur = 1400;
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / dur);
          // Expo-out, matched to --pg-m-ease.
          const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
          const value = Math.round(target * eased);
          el.textContent = pad ? String(value).padStart(Number(pad), '0') : String(value);
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    }, { threshold: 0.6 });
    obs.observe(el);
  };

  /* =======================================================================
     CURSOR
     ======================================================================= */
  /**
   * Contextual label, shown only over elements carrying data-pg-cursor.
   *
   * The previous version was an always-on follower: two elements lerping on
   * every frame for the whole session, drawn with mix-blend-mode: difference.
   * That blend mode forces the browser to recomposite everything beneath the
   * cursor on every pointer move, which was the single most expensive thing
   * in this file. It also replaced the native cursor, which is a liability on
   * a store. This version keeps the native cursor, attaches no global
   * pointermove listener, and does no per-frame work unless the pointer is
   * actually over media that asked for a label.
   */
  const buildCursor = () => {
    if (!finePointer.matches || tier === 'low') return;
    const targets = document.querySelectorAll('[data-pg-cursor]');
    if (!targets.length) return;

    const el = document.createElement('div');
    el.className = 'pg-cursor';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<span class="pg-cursor__ring"><span class="pg-cursor__label"></span></span>';
    document.body.appendChild(el);

    const label = el.querySelector('.pg-cursor__label');
    const state = { x: 0, y: 0, tx: 0, ty: 0 };
    let active = false;

    const follow = (delta) => {
      if (!active) {
        frameTasks.delete(follow);
        return;
      }
      const lerp = 1 - Math.exp(-18 * delta);
      state.x += (state.tx - state.x) * lerp;
      state.y += (state.ty - state.y) * lerp;
      el.style.transform =
        `translate3d(${Math.round(state.x)}px, ${Math.round(state.y)}px, 0)`;
    };

    targets.forEach((target) => {
      target.addEventListener('pointerenter', (event) => {
        active = true;
        state.x = state.tx = event.clientX;
        state.y = state.ty = event.clientY;
        const text = target.getAttribute('data-pg-cursor') || '';
        if (label.textContent !== text) label.textContent = text;
        el.style.transform =
          `translate3d(${Math.round(state.x)}px, ${Math.round(state.y)}px, 0)`;
        el.classList.add('is-active');
        if (!frameTasks.has(follow)) addFrameTask(follow);
      });

      target.addEventListener('pointermove', (event) => {
        state.tx = event.clientX;
        state.ty = event.clientY;
      }, { passive: true });

      target.addEventListener('pointerleave', () => {
        active = false;
        el.classList.remove('is-active');
      });
    });
  };
  /* =======================================================================
     BOOT
     ======================================================================= */
  const scan = (scope) => {
    // .pg-reveal is the earlier, simpler reveal used across the standard
    // pages. It is observed by this engine too, so there is one scheduler for
    // the whole site rather than a second IntersectionObserver in pages.js.
    scope.querySelectorAll('[data-pg-m], .pg-reveal').forEach((el) => {
      const delay = el.getAttribute('data-pg-m-delay');
      if (delay) el.style.setProperty('--pg-m-delay', delay + 'ms');
      if (io) io.observe(el);
      else el.classList.add('is-in');
    });

    scope.querySelectorAll('[data-pg-split]').forEach((el) => splitTargets.push(el));
    scope.querySelectorAll('[data-pg-parallax]').forEach(registerParallax);
    scope.querySelectorAll('[data-pg-magnetic]').forEach(registerMagnet);
    scope.querySelectorAll('[data-pg-count]').forEach(registerCounter);
  };

  /* Parallax is driven from scrollTask so it shares the same wake/sleep loop. */
  const runParallax = (delta) => {
    if (!parallaxItems.length) return;
    const viewport = window.innerHeight;
    const lerp = 1 - Math.exp(-9 * delta);
    for (const item of parallaxItems) {
      if (!item.rect) continue;
      const centre = item.rect.top + item.rect.height / 2 - scroll.y - viewport / 2;
      // Skip anything well outside the viewport: no reason to write to it.
      if (Math.abs(centre) > viewport * 1.4) continue;
      item.target = -centre * item.depth;
      item.current += (item.target - item.current) * lerp;
      item.el.style.transform =
        `translate3d(0, ${Math.round(item.current * 100) / 100}px, 0)`;
    }
  };

  const parallaxSettled = () => {
    for (const item of parallaxItems) {
      if (Math.abs(item.target - item.current) > 0.3) return false;
    }
    return true;
  };

  /* Magnets compute only while one is actually hovered, then stop. */
  const magnetTask = (delta) => {
    const lerp = 1 - Math.exp(-9 * delta);
    let busy = false;
    for (const m of magnets) {
      m.x += (m.tx - m.x) * lerp;
      m.y += (m.ty - m.y) * lerp;
      if (!m.active && Math.abs(m.x) < 0.2 && Math.abs(m.y) < 0.2) {
        if (m.x !== 0 || m.y !== 0) {
          m.x = 0;
          m.y = 0;
          // Land on exactly zero and drop the layer, so button labels get
          // their subpixel antialiasing back at rest.
          m.el.style.transform = '';
          releaseLayer(m.el);
        }
        continue;
      }
      busy = true;
      m.el.style.transform =
        `translate3d(${Math.round(m.x * 100) / 100}px, ${Math.round(m.y * 100) / 100}px, 0)`;
    }
    if (!busy) frameTasks.delete(magnetTask);
  };

  const wakeMagnets = () => {
    if (!frameTasks.has(magnetTask)) addFrameTask(magnetTask);
  };

  const startParallax = () => {
    if (!parallaxItems.length) return;
    measureParallax();
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measureParallax, 160);
    }, { passive: true });
    wakeScroll();
  };

  const boot = () => {
    scan(document);
    root.classList.add('pg-m-ready');

    /**
     * Split immediately, then again once the real face resolves.
     *
     * Waiting for fonts.ready before the first split leaves a window where the
     * heading paints as normal text and then visibly jumps when the lines are
     * wrapped and hidden. Splitting now removes that flash; re-splitting after
     * the font lands fixes the line boxes, which are measured against whatever
     * face was active at the time.
     *
     * Elements already revealed keep their finished state through a re-split,
     * so nothing re-animates under the reader.
     */
    const resplit = () => {
      splitTargets.forEach((el) => {
        const wasIn = el.classList.contains('is-in');
        splitLines(el);
        applyLineDelays(el);
        if (wasIn) {
          el.classList.add('is-in');
          el.querySelectorAll('.pg-line').forEach((l) => { l.style.overflow = 'visible'; });
          el.querySelectorAll('.pg-line__i').forEach((i) => { i.style.transform = 'none'; });
        } else if (io && !revealed.has(el)) {
          io.observe(el);
        }
      });
    };

    resplit();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(resplit).catch(() => {});
    }

    // Line boxes are width dependent, so re-measure on real width changes.
    // Height-only changes (mobile URL bar) are ignored.
    let splitTimer;
    let lastWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      clearTimeout(splitTimer);
      splitTimer = setTimeout(resplit, 200);
    }, { passive: true });

    startParallax();
    buildCursor();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
